import { Component, MeshComponent, Property } from '@wonderlandengine/api';
import { vec3, mat3, quat } from 'gl-matrix';

/**
 * bullet.js (VoxelVerse)
 *
 * Pooled kinematic bullet tailored for VoxelVerse integration. Mirrors the
 * original Arena-Wars `kinematic-bullet` behaviour but exposes a few hooks:
 * - `damage` property
 * - `fireWithShooter(linVel, shooterId)` convenience
 * - on-update voxel hit application against `window.VoxelTreadmill._applyHitAtWorld`
 */
export class VoxelBullet extends Component {
    static TypeName = 'voxel-bullet';
    static Properties = {
        maxLifetime: Property.float(2.0),
        bulletSpeedScale: Property.float(0.5),
        damage: Property.float(10.0),
    };

    // ── Static Math Pool ──────────────────────────────────────────────────────
    static _vDir    = vec3.create();
    static _vRight  = vec3.create();
    static _vUp     = vec3.create();
    static _mRot    = mat3.create();
    static _qRot    = quat.create();
    static _worldUp = vec3.fromValues(0, 1, 0);
    static _altUp   = vec3.fromValues(1, 0, 0);

    init() {
        this.shooterId = null;
        this._isActive = false;
        this._lifeTimer = 0;
        this.originalScaling = vec3.fromValues(0.025, 0.025, 0.420);
        this._velocity = new Float32Array(3);
        this._currentPos = vec3.create();
        this._targetPos = vec3.create();
        this._delta = new Float32Array(3);
        this._hiddenPos = new Float32Array([0, -1000, 0]);
    }

    start() {
        this.object.setScalingWorld(this.originalScaling);
        if (this.engine?.onXRSessionEnd) {
            this._boundXREnd = this._resetSession.bind(this);
            this.engine.onXRSessionEnd.add(this._boundXREnd);
        }
        this._boundDesktopEnd = this._resetSession.bind(this);
        window.addEventListener('desktop-session-end', this._boundDesktopEnd);
        this.object.setPositionWorld(this._hiddenPos);
        this.object.active = false; // hide mesh + pause all components on this object
    }

    onDestroy() {
        if (this._boundXREnd && this.engine?.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundXREnd);
        }
        if (this._boundDesktopEnd) window.removeEventListener('desktop-session-end', this._boundDesktopEnd);
    }

    fire(origin, velocity) {
        // object.active is already set to true by fps-pistol before calling fire().
        // Set it again here as a safety net (no-op if already active).
        this.object.active = true;

        // Ensure the bullet is parented to the treadmill so it moves with the voxel world
        const vt = window.VoxelTreadmill;
        if (vt?.object && this.object.parent !== vt.object) {
            this.object.parent = vt.object;
        }

        // Parent is the treadmill: local coordinates are true world coordinates.
        // Use setPositionWorld (WLE handles local conversion) and convert velocity to local basis.
        this.object.setPositionWorld(origin);
        const vtRot = VoxelBullet._qRot;
        this.object.parent ? this.object.parent.getRotationWorld(vtRot) : quat.identity(vtRot);
        quat.conjugate(vtRot, vtRot);
        vec3.transformQuat(this._velocity, velocity, vtRot);
        vec3.scale(this._velocity, this._velocity, this.bulletSpeedScale);

        this._lifeTimer = 0;

        // ── CONSTRUCT ROTATION FROM BASIS ─────────────────────────────────────
        // Replacing .lookAt() with an explicit orthonormal basis to avoid
        // gimbal lock and cross-product collapse at steep vertical angles.
        const forward = VoxelBullet._vDir;
        vec3.normalize(forward, this._velocity);

        const right = VoxelBullet._vRight;
        const up    = VoxelBullet._vUp;
        const worldUp = Math.abs(forward[1]) > 0.99 ? VoxelBullet._altUp : VoxelBullet._worldUp;

        // Orthonormal basis: Right = Forward x Up; Up' = Right x Forward
        vec3.cross(right, forward, worldUp);
        vec3.normalize(right, right);
        vec3.cross(up, right, forward);
        vec3.normalize(up, up);

        // Rotation Matrix (Column-Major): [Right, Up, Backward]
        // Backward = -Forward, because WLE uses -Z as the forward axis.
        const m = VoxelBullet._mRot;
        m[0] = right[0]; m[1] = right[1]; m[2] = right[2]; // Col 0 (Local X)
        m[3] = up[0];    m[4] = up[1];    m[5] = up[2];    // Col 1 (Local Y)
        m[6] = -forward[0]; m[7] = -forward[1]; m[8] = -forward[2]; // Col 2 (Local Z)

        const q = VoxelBullet._qRot;
        quat.fromMat3(q, m);
        this.object.setRotationLocal(q);

        this._isActive = true;
    }

    fireWithShooter(origin, direction, shooterId) {
        this.fire(origin, direction);
        this.shooterId = shooterId || null;
    }

    deactivateBullet() {
        if (!this._isActive) return;
        this.object.setScalingWorld(this.originalScaling);
        this._isActive = false;
        this.object.active = false; // hide mesh + pause all components
        this.shooterId = null;
        this._velocity[0] = 0; this._velocity[1] = 0; this._velocity[2] = 0;
        this._lifeTimer = 0;
        this.object.setPositionWorld(this._hiddenPos);
    }

    update(dt) {
        if (window.GameState !== 'playing') return;
        if (!this._isActive) return;
        this._lifeTimer += dt;
        if (this._lifeTimer >= this.maxLifetime) { this.deactivateBullet(); return; }
        vec3.scale(this._delta, this._velocity, dt);
        this.object.translate(this._delta);

        // Try applying voxel hit (non-fatal if treadmill unavailable)
        try {
            if (window.VoxelTreadmill) {
                this.object.getPositionLocal(this._currentPos);
                
                // Check if we hit something solid (getPositionLocal is world space for treadmill children)
                if (window.VoxelTreadmill._isSolid(this._currentPos[0], this._currentPos[1], this._currentPos[2])) {
                    // Try damaging foliage, but do NOT damage terrain (no mining)
                    try {
                        if (window.VoxelFoliage && typeof window.VoxelFoliage.applyHitAtWorld === 'function') {
                            const foliageHit = window.VoxelFoliage.applyHitAtWorld(this._currentPos, this.damage, this.shooterId);
                            if (foliageHit) this._pulseCrosshair();
                        }
                    } catch (_) {}

                    // Deactivate bullet on impact with any solid surface
                    this.deactivateBullet();
                    return;
                }
            }
        } catch (e) { try { console.error('[VoxelBullet] _applyHitAtWorld error', e); } catch (_) {} }
    }

    _pulseCrosshair() {
        try {
            if (window.GameInterface && typeof window.GameInterface.pulseCrosshair === 'function') {
                window.GameInterface.pulseCrosshair();
            } else {
                const ch = document.getElementById('crosshair');
                if (ch) {
                    ch.classList.add('hit');
                    setTimeout(() => { try { ch.classList.remove('hit'); } catch(_){} }, 120);
                }
            }
        } catch (_) {}
    }

    _resetSession() {
        this._isActive = false;
        this.object.active = false;
        this.shooterId = null;
        this._velocity[0] = 0; this._velocity[1] = 0; this._velocity[2] = 0;
        this._lifeTimer = 0;
        this.object.setPositionWorld(this._hiddenPos);
    }
}